/**
 * Non-blocking progress toast (same chrome as manual backups).
 * Does not trap the user in a modal or full-screen overlay.
 */
import { toast } from "sonner";

const BAR_CLASS =
  "h-full rounded-full bg-[#1976D2] transition-[width] duration-200 ease-out dark:bg-blue-500";

const TICK_MS = 200;
const STAGE_MS = 4500;

export type ProgressStage = { label: string; targetPct: number };

export type ProgressToastSnapshot = {
  id: string;
  title: string;
  stage: string;
  pct: number;
} | null;

let liveToast: ProgressToastSnapshot = null;

/** Estado del overlay bottom-right (backups, stock, cierre export). */
export function getProgressToastSnapshot(): ProgressToastSnapshot {
  return liveToast;
}

export function renderProgressToast(
  id: string,
  pct: number,
  stage: string,
  title: string,
) {
  const safePct = Math.min(100, Math.max(0, pct));
  liveToast = { id, title, stage, pct: safePct };
  toast.custom(
    () => (
      <div className="pointer-events-auto w-[min(22rem,calc(100vw-2rem))] rounded-lg border border-slate-200 bg-white p-3 shadow-lg dark:border-slate-700 dark:bg-slate-900">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{title}</p>
          <span className="shrink-0 text-xs tabular-nums text-slate-500 dark:text-slate-400">
            {safePct}%
          </span>
        </div>
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
          <div className={BAR_CLASS} style={{ width: `${safePct}%` }} />
        </div>
        <p className="mt-1.5 truncate text-xs text-slate-500 dark:text-slate-400">{stage}</p>
      </div>
    ),
    {
      id,
      duration: Infinity,
      position: "bottom-right",
    },
  );
}

export function dismissProgressToast(id: string) {
  if (liveToast?.id === id) liveToast = null;
  toast.dismiss(id);
}

/** Fake staged bar (caps at 90% until the job resolves), then 100% and dismiss. */
export async function runWithProgressToast<T>(opts: {
  id: string;
  title: string;
  stages: readonly ProgressStage[];
  job: () => Promise<T>;
}): Promise<T> {
  const stages = opts.stages;
  let displayPct = 4;
  let stageIdx = 0;
  let animDone = false;
  const startedAt = Date.now();
  const firstLabel = stages[0]?.label ?? opts.title;
  renderProgressToast(opts.id, 4, firstLabel, opts.title);

  const timer = setInterval(() => {
    if (animDone) return;
    const elapsed = Date.now() - startedAt;
    stageIdx = Math.min(
      Math.floor(elapsed / STAGE_MS),
      Math.max(0, stages.length - 1),
    );
    const target = stages[stageIdx]?.targetPct ?? 90;
    displayPct = displayPct + Math.max(0.25, (target - displayPct) * 0.1);
    if (displayPct > target) displayPct = target;
    const pct = Math.min(Math.round(displayPct), 90);
    const label = stages[stageIdx]?.label ?? opts.title;
    renderProgressToast(opts.id, pct, label, opts.title);
  }, TICK_MS);

  try {
    const result = await opts.job();
    animDone = true;
    renderProgressToast(opts.id, 100, "¡Completado!", opts.title);
    await new Promise((r) => setTimeout(r, 450));
    dismissProgressToast(opts.id);
    return result;
  } catch (err) {
    animDone = true;
    dismissProgressToast(opts.id);
    throw err;
  } finally {
    clearInterval(timer);
  }
}
